import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from './Header';
import { API_URL, BASE_URL, BROKEN_IMAGE_PLACEHOLDER, getImageUrl } from '../config';
import './SellerOrdersPage.css';

const getOrderDisplayNumber = (order) => {
  if (!order) return '';
  const stored = order.orderNumber || order.OrderNumber;
  if (stored) return stored;
  const type = order.orderType || order.OrderType;
  const offlineId = order.offlineTransactionId || order.OfflineTransactionId;
  if (type === 'Offline' || offlineId) return `OFF-${offlineId}`;
  const orderId = order.orderId || order.OrderId;
  if (orderId) return `ORD-${orderId}`;
  return '';
};

const toPlainProductText = (raw) => {
  if (raw == null || typeof raw !== 'string') return '';
  let s = raw.replace(/<[^>]*>/g, ' ');
  if (typeof document !== 'undefined') {
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    s = ta.value;
  }
  s = s.replace(/\s+/g, ' ').trim();
  if (!s) return '';
  // Hide other-script or placeholder copy on seller order details.
  if (/[\u0900-\u097F\u0A80-\u0AFF\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0D80-\u0DFF\u0E00-\u0E7F\u4E00-\u9FFF]/.test(s)) {
    return '';
  }
  const letters = (s.match(/[A-Za-z]/g) || []).length;
  if (letters < 8) return '';
  return s;
};

const SellerOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [deliveryStatuses, setDeliveryStatuses] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    expectedDeliveryDate: '',
    deliveryStatus: '',
    trackingNumber: '',
    deliveryNotes: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const detailPanelRef = useRef(null);
  const pendingScrollToDetail = useRef(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchOrders();
    fetchDeliveryStatuses();
  }, [statusFilter, fromDate, toDate]);

  const fetchDeliveryStatuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/SellerOrders/delivery-statuses`, {
        headers: getAuthHeader()
      });
      const data = response.data;
      setDeliveryStatuses(Array.isArray(data) ? data : (data?.$values || []));
    } catch (err) {
      console.error('Error fetching statuses:', err);
      setDeliveryStatuses([]);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      const response = await axios.get(`${API_URL}/SellerOrders`, {
        headers: getAuthHeader(),
        params
      });
      
      const ordersData = response.data.orders;
      const list = Array.isArray(ordersData) ? ordersData : (ordersData?.$values || []);
      setOrders(list);
      setSummary(response.data.summary);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedOrder?.orderItem || !pendingScrollToDetail.current) return;
    pendingScrollToDetail.current = false;
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 900px)').matches) return;
    let timeoutId;
    const frame = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => {
        detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [selectedOrder]);

  const fetchOrderDetail = async (order) => {
    try {
      if (order.orderType === 'Offline' && order.offlineTransactionId) {
        const response = await axios.get(`${API_URL}/SellerOrders/offline/${order.offlineTransactionId}`, {
          headers: getAuthHeader()
        });
        setSelectedOrder(response.data);
        return;
      }
      const response = await axios.get(`${API_URL}/SellerOrders/${order.orderItemId}`, {
        headers: getAuthHeader()
      });
      setSelectedOrder(response.data);
    } catch (err) {
      console.error('Error fetching order detail:', err);
    }
  };

  const handleOfflineStatus = async (id, status) => {
    const notes = status === 'Rejected' ? window.prompt('Rejection comment (optional):', '') : '';
    if (status === 'Rejected' && notes === null) return;
    if (status === 'Approved' && !window.confirm('Approve this offline purchase?')) return;
    setActionLoading(true);
    try {
      await axios.put(
        `${API_URL}/OfflineTransaction/seller/${id}/status`,
        { status, notes: notes || undefined },
        { headers: getAuthHeader() }
      );
      fetchOrders();
      const response = await axios.get(`${API_URL}/SellerOrders/offline/${id}`, { headers: getAuthHeader() });
      setSelectedOrder(response.data);
    } catch (err) {
      alert(err.response?.data?.message || `Failed to ${status.toLowerCase()} offline purchase`);
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = (order) => {
    setEditForm({
      expectedDeliveryDate: order.expectedDeliveryDate ? order.expectedDeliveryDate.split('T')[0] : '',
      deliveryStatus: order.deliveryStatus || 'Pending',
      trackingNumber: order.trackingNumber || '',
      deliveryNotes: order.deliveryNotes || '',
      notes: ''
    });
    setShowEditModal(true);
  };

  const handleUpdateDelivery = async () => {
    if (!selectedOrder?.orderItem) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/SellerOrders/${selectedOrder.orderItem.orderItemId}/delivery`,
        {
          expectedDeliveryDate: editForm.expectedDeliveryDate || null,
          deliveryStatus: editForm.deliveryStatus,
          trackingNumber: editForm.trackingNumber || null,
          deliveryNotes: editForm.deliveryNotes || null,
          notes: editForm.notes || null
        },
        { headers: getAuthHeader() }
      );
      
      const isCancelled = /cancel/i.test(String(editForm.deliveryStatus || ''));
      alert(isCancelled
        ? 'Order cancelled. Points used or earned on this order have been reversed.'
        : 'Delivery information updated successfully!');
      setShowEditModal(false);
      fetchOrders();
      fetchOrderDetail(selectedOrder.orderItem);
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data || 'Failed to update delivery information');
    } finally {
      setSaving(false);
    }
  };

  const openRejectModal = () => {
    setRejectComment('');
    setShowRejectModal(true);
  };

  const handleRejectOrder = async () => {
    if (!selectedOrder?.orderItem) return;
    const comment = rejectComment.trim();
    if (!comment) {
      alert('Please enter a rejection comment. The customer will see this note.');
      return;
    }
    setRejecting(true);
    try {
      await axios.post(
        `${API_URL}/SellerOrders/${selectedOrder.orderItem.orderItemId}/reject`,
        { comment },
        { headers: getAuthHeader() }
      );
      alert('Order rejected. The customer can see your comment, and points have been reversed.');
      setShowRejectModal(false);
      setRejectComment('');
      fetchOrders();
      fetchOrderDetail(selectedOrder.orderItem);
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data || 'Failed to reject order');
    } finally {
      setRejecting(false);
    }
  };

  const openInvoice = async () => {
    const item = selectedOrder?.orderItem;
    if (!item) return;
    const isOffline = selectedOrder.orderType === 'Offline' || item.orderType === 'Offline';
    setInvoiceLoading(true);
    setShowInvoice(true);
    setInvoice(null);
    try {
      const url = isOffline
        ? `${API_URL}/SellerOrders/invoice/offline/${item.offlineTransactionId}`
        : `${API_URL}/SellerOrders/invoice/online/${item.orderId}`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setInvoice(response.data);
    } catch (err) {
      setShowInvoice(false);
      alert(err.response?.data?.message || err.response?.data || 'Failed to load invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const printInvoice = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderStatus = (order) => {
    const orderStatus = order?.orderStatus || order?.OrderStatus || '';
    const deliveryStatus = order?.deliveryStatus || order?.DeliveryStatus || '';
    if (/reject/i.test(String(orderStatus)) || /reject/i.test(String(deliveryStatus))) {
      return 'Rejected';
    }
    if (String(orderStatus).toLowerCase() === 'cancelled' || String(deliveryStatus).toLowerCase() === 'cancelled') {
      return 'Cancelled';
    }
    return deliveryStatus || orderStatus || 'Pending';
  };

  const getStatusClass = (status) => {
    return `status-${(status || 'pending').toLowerCase().replace(/\s+/g, '')}`;
  };

  if (loading && orders.length === 0) {
    return (
      <div className="seller-orders-wrapper">
        <Header />
        <div className="seller-orders-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="seller-orders-wrapper">
      <Header />
      <div className="seller-orders-container">
        <div className="page-header">
          <h1>Order Management</h1>
          <p>Manage online product orders and offline store purchases</p>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {/* Summary Cards - database-driven status counts, always reflect latest from server */}
        {summary && (
          <div className="summary-cards">
            <div className="summary-card total">
              <span className="card-value">{summary.totalOrders ?? summary.TotalOrders ?? 0}</span>
              <span className="card-label">Total Orders</span>
            </div>
            <div className="summary-card pending">
              <span className="card-value">{summary.pendingCount ?? summary.PendingCount ?? 0}</span>
              <span className="card-label">Pending</span>
            </div>
            <div className="summary-card processing">
              <span className="card-value">{summary.processingCount ?? summary.ProcessingCount ?? 0}</span>
              <span className="card-label">Processing</span>
            </div>
            <div className="summary-card shipped">
              <span className="card-value">{summary.shippedCount ?? summary.ShippedCount ?? 0}</span>
              <span className="card-label">Shipped</span>
            </div>
            <div className="summary-card outfordelivery">
              <span className="card-value">{summary.outForDeliveryCount ?? summary.OutForDeliveryCount ?? 0}</span>
              <span className="card-label">Out for Delivery</span>
            </div>
            <div className="summary-card delivered">
              <span className="card-value">{summary.deliveredCount ?? summary.DeliveredCount ?? 0}</span>
              <span className="card-label">Delivered</span>
            </div>
            <div className="summary-card cancelled">
              <span className="card-value">{summary.cancelledCount ?? summary.CancelledCount ?? 0}</span>
              <span className="card-label">Cancelled</span>
            </div>
            <div className="summary-card rejected">
              <span className="card-value">{summary.rejectedCount ?? summary.RejectedCount ?? 0}</span>
              <span className="card-label">Rejected</span>
            </div>
            <div className="summary-card pending">
              <span className="card-label">Offline purchases</span>
            </div>
            <div className="summary-card revenue">
              <span className="card-value">₹{(summary.totalRevenue ?? summary.TotalRevenue ?? 0)?.toFixed(2)}</span>
              <span className="card-label">Total Revenue</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="filters-section">
          <div className="filter-group">
            <label>From date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={toDate || undefined}
            />
          </div>
          <div className="filter-group">
            <label>To date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || undefined}
            />
          </div>
          <div className="filter-group">
            <label>Order type</label>
            <select
              value={orderTypeFilter}
              onChange={(e) => setOrderTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              <option value="Online">Online</option>
              <option value="Offline">Offline purchase</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Delivery status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {deliveryStatuses.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group filter-actions">
            <button
              type="button"
              className="clear-filters-btn"
              onClick={() => { setFromDate(''); setToDate(''); setStatusFilter(''); setOrderTypeFilter(''); }}
              disabled={!fromDate && !toDate && !statusFilter && !orderTypeFilter}
            >
              Clear filters
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="orders-content">
          {/* Orders List */}
          <div className="orders-list-panel">
            <h3>Orders ({(orderTypeFilter ? orders.filter((o) => o.orderType === orderTypeFilter) : orders).length})</h3>
            {(orderTypeFilter ? orders.filter((o) => o.orderType === orderTypeFilter) : orders).length === 0 ? (
              <div className="empty-state">
                <p>No orders found</p>
              </div>
            ) : (
              <div className="orders-list">
                {(orderTypeFilter ? orders.filter((o) => o.orderType === orderTypeFilter) : orders).map((order) => {
                  const isOffline = order.orderType === 'Offline';
                  const isActive = isOffline
                    ? selectedOrder?.orderItem?.offlineTransactionId === order.offlineTransactionId
                    : selectedOrder?.orderItem?.orderItemId === order.orderItemId;
                  return (
                  <div
                    key={isOffline ? `off-${order.offlineTransactionId}` : order.orderItemId}
                    className={`order-card ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      pendingScrollToDetail.current = true;
                      fetchOrderDetail(order);
                    }}
                  >
                    <div className="order-card-header">
                      <span className="order-id">{getOrderDisplayNumber(order)}</span>
                      <span className={`order-type-badge ${isOffline ? 'offline' : 'online'}`}>{isOffline ? 'Offline' : 'Online'}</span>
                      <span className={`delivery-status ${getStatusClass(getOrderStatus(order))}`}>
                        {getOrderStatus(order)}
                      </span>
                    </div>
                    <div className="order-card-body">
                      <div className="product-info">
                        <img 
                          src={getImageUrl(order.productImage)} 
                          alt={order.productName}
                          onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                        />
                        <div className="product-details">
                          <span className="product-name">{order.productName}</span>
                          {(order.sizeName || order.colorName) && (
                            <span className="product-qty">
                              {[order.sizeName && `Size: ${order.sizeName}`, order.colorName && `Color: ${order.colorName}`].filter(Boolean).join(' · ')}
                            </span>
                          )}
                          {isOffline ? (
                            <span className="product-qty">{order.customer?.fullName || order.customer?.username || 'Customer'}</span>
                          ) : (
                            <span className="product-qty">Qty: {order.quantity}</span>
                          )}
                        </div>
                      </div>
                      <div className="order-meta">
                        <span className="order-date">{formatDate(order.orderDate)}</span>
                        <span className="order-total">₹{order.totalPrice?.toFixed(2)}</span>
                      </div>
                      {order.expectedDeliveryDate && (
                        <div className="expected-delivery">
                          Expected: {formatDate(order.expectedDeliveryDate)}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Order Detail Panel */}
          <div className="order-detail-panel" ref={detailPanelRef} id="seller-order-detail">
            {selectedOrder?.orderItem ? (
              <>
                <div className="detail-header">
                  <h2>{getOrderDisplayNumber(selectedOrder.orderItem)}</h2>
                  <div className="detail-header-actions">
                    <button type="button" className="invoice-btn" onClick={openInvoice}>
                      View Invoice
                    </button>
                    {(selectedOrder.orderType === 'Offline' || selectedOrder.orderItem.orderType === 'Offline') ? (
                      selectedOrder.orderItem.deliveryStatus === 'Pending' ? (
                        <div className="offline-actions">
                          <button
                            type="button"
                            className="edit-btn"
                            disabled={actionLoading}
                            onClick={() => handleOfflineStatus(selectedOrder.orderItem.offlineTransactionId, 'Approved')}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="reject-offline-btn"
                            disabled={actionLoading}
                            onClick={() => handleOfflineStatus(selectedOrder.orderItem.offlineTransactionId, 'Rejected')}
                          >
                            Reject
                          </button>
                        </div>
                      ) : null
                    ) : !['Cancelled', 'Rejected'].includes(getOrderStatus(selectedOrder.orderItem)) ? (
                      <div className="offline-actions">
                      <button
                        className="edit-btn"
                        onClick={() => openEditModal(selectedOrder.orderItem)}
                      >
                        Update Delivery
                      </button>
                      <button
                        type="button"
                        className="reject-offline-btn"
                        onClick={openRejectModal}
                      >
                        Reject Order
                      </button>
                      </div>
                    ) : null}
                  </div>
                </div>

                {(selectedOrder.orderType === 'Offline' || selectedOrder.orderItem.orderType === 'Offline') && (
                  <div className="detail-section">
                    <h4>Offline purchase</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">Status</span>
                        <span className={`value status-badge ${getStatusClass(getOrderStatus(selectedOrder.orderItem))}`}>
                          {getOrderStatus(selectedOrder.orderItem)}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="label">Amount</span>
                        <span className="value">₹{Number(selectedOrder.orderItem.totalPrice || 0).toFixed(2)}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Purchase date</span>
                        <span className="value">{formatDate(selectedOrder.orderItem.orderDate)}</span>
                      </div>
                      {selectedOrder.orderItem.transactionReference && (
                        <div className="info-item">
                          <span className="label">Reference</span>
                          <span className="value">{selectedOrder.orderItem.transactionReference}</span>
                        </div>
                      )}
                      {selectedOrder.orderItem.adminNotes && (
                        <div className="info-item full-width">
                          <span className="label">Notes</span>
                          <span className="value">{selectedOrder.orderItem.adminNotes}</span>
                        </div>
                      )}
                    </div>
                    {selectedOrder.orderItem.receiptImageUrl && (
                      <div className="product-detail-card" style={{ marginTop: 12 }}>
                        <a href={getImageUrl(selectedOrder.orderItem.receiptImageUrl)} target="_blank" rel="noopener noreferrer">
                          <img
                            src={getImageUrl(selectedOrder.orderItem.receiptImageUrl)}
                            alt="Receipt"
                            onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                          />
                        </a>
                        <div className="product-info">
                          <h5>Bill / receipt</h5>
                          <p className="product-desc">Click the image to view the uploaded bill.</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(selectedOrder.orderType !== 'Offline' && selectedOrder.orderItem.orderType !== 'Offline') && (
                <>
                {/* Product Info */}
                <div className="detail-section">
                  <h4>Product</h4>
                  <div className="product-detail-card">
                    <img 
                      src={getImageUrl(selectedOrder.orderItem.productImage)} 
                      alt={selectedOrder.orderItem.productName}
                      onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                    />
                    <div className="product-info">
                      <h5>{selectedOrder.orderItem.productName}</h5>
                      {(selectedOrder.orderItem.sizeName || selectedOrder.orderItem.colorName) && (
                        <p className="product-desc">
                          {[selectedOrder.orderItem.sizeName && `Size: ${selectedOrder.orderItem.sizeName}`, selectedOrder.orderItem.colorName && `Color: ${selectedOrder.orderItem.colorName}`].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {toPlainProductText(selectedOrder.orderItem.productDescription) ? (
                        <p className="product-desc">{toPlainProductText(selectedOrder.orderItem.productDescription)}</p>
                      ) : null}
                      <div className="price-info">
                        <span>Qty: {selectedOrder.orderItem.quantity}</span>
                        <span>₹{selectedOrder.orderItem.unitPrice?.toFixed(2)} each</span>
                        <strong>Total: ₹{selectedOrder.orderItem.totalPrice?.toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="detail-section">
                  <h4>Delivery Information</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="label">Status</span>
                      <span className={`value status-badge ${getStatusClass(getOrderStatus(selectedOrder.orderItem))}`}>
                        {getOrderStatus(selectedOrder.orderItem)}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="label">Expected Delivery</span>
                      <span className="value">{formatDate(selectedOrder.orderItem.expectedDeliveryDate)}</span>
                    </div>
                    {selectedOrder.orderItem.actualDeliveryDate && (
                      <div className="info-item">
                        <span className="label">Actual Delivery</span>
                        <span className="value">{formatDate(selectedOrder.orderItem.actualDeliveryDate)}</span>
                      </div>
                    )}
                    {selectedOrder.orderItem.trackingNumber && (
                      <div className="info-item">
                        <span className="label">Tracking Number</span>
                        <span className="value tracking">{selectedOrder.orderItem.trackingNumber}</span>
                      </div>
                    )}
                    {selectedOrder.orderItem.deliveryNotes && (
                      <div className="info-item full-width">
                        <span className="label">{getOrderStatus(selectedOrder.orderItem) === 'Rejected' ? 'Rejection comment' : 'Delivery Notes'}</span>
                        <span className="value">{selectedOrder.orderItem.deliveryNotes}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <span className="label">Last Updated</span>
                      <span className="value">{formatDateTime(selectedOrder.orderItem.lastUpdatedAt)}</span>
                    </div>
                  </div>
                </div>
                </>
                )}

                {/* Customer Info */}
                <div className="detail-section">
                  <h4>Customer Details</h4>
                  <div className="customer-card">
                    <div className="customer-header">
                      <div className="customer-avatar">
                        {selectedOrder.orderItem.customer?.username?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div className="customer-info">
                        <span className="customer-name">
                          {selectedOrder.orderItem.customer?.fullName || selectedOrder.orderItem.customer?.username}
                        </span>
                        {selectedOrder.orderItem.customer?.referralCode && (
                          <span className="customer-email">Code: {selectedOrder.orderItem.customer.referralCode}</span>
                        )}
                        <span className="customer-email">{selectedOrder.orderItem.customer?.email}</span>
                      </div>
                    </div>
                    <div className="customer-details">
                      {selectedOrder.orderItem.customer?.phoneNumber && (
                        <div className="detail-row">
                          <span className="icon">📞</span>
                          <span>{selectedOrder.orderItem.customer.phoneNumber}</span>
                          {selectedOrder.orderItem.customer.alternatePhoneNumber && (
                            <span className="alt-phone">, {selectedOrder.orderItem.customer.alternatePhoneNumber}</span>
                          )}
                        </div>
                      )}
                      {selectedOrder.orderItem.customer?.address && (
                        <div className="detail-row">
                          <span className="icon">📍</span>
                          <span>
                            {selectedOrder.orderItem.customer.address}
                            {selectedOrder.orderItem.customer.addressLine2 && `, ${selectedOrder.orderItem.customer.addressLine2}`}
                            {selectedOrder.orderItem.customer.landmark && ` (${selectedOrder.orderItem.customer.landmark})`}
                            <br />
                            {selectedOrder.orderItem.customer.city}, {selectedOrder.orderItem.customer.state} - {selectedOrder.orderItem.customer.pinCode}
                            {selectedOrder.orderItem.customer.country && `, ${selectedOrder.orderItem.customer.country}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Delivery History */}
                {selectedOrder.deliveryHistory?.length > 0 && (
                  <div className="detail-section">
                    <h4>Delivery History</h4>
                    <div className="history-list">
                      {(Array.isArray(selectedOrder.deliveryHistory) 
                        ? selectedOrder.deliveryHistory 
                        : selectedOrder.deliveryHistory.$values || []
                      ).map((h, idx) => (
                        <div key={h.historyId || idx} className="history-item">
                          <div className="history-icon">
                            <div className="dot"></div>
                            {idx < selectedOrder.deliveryHistory.length - 1 && <div className="line"></div>}
                          </div>
                          <div className="history-content">
                            <div className="history-header">
                              <span className="status-change">
                                {h.oldStatus && <span className="old">{h.oldStatus}</span>}
                                {h.oldStatus && ' → '}
                                <span className="new">{h.newStatus}</span>
                              </span>
                              <span className="history-date">{formatDateTime(h.changedAt)}</span>
                            </div>
                            {h.newDeliveryDate && h.newDeliveryDate !== h.oldDeliveryDate && (
                              <p className="date-change">
                                Delivery date: {formatDate(h.newDeliveryDate)}
                              </p>
                            )}
                            {h.notes && <p className="history-notes">{h.notes}</p>}
                            <span className="changed-by">by {h.changedBy}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-selection">
                <svg viewBox="0 0 24 24" fill="currentColor" width="60" height="60">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
                <p>Select an order to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Delivery Information</h3>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Delivery Status</label>
                <select
                  value={editForm.deliveryStatus}
                  onChange={(e) => setEditForm({ ...editForm, deliveryStatus: e.target.value })}
                >
                  {deliveryStatuses.filter((s) => !/reject/i.test(String(s.value || s.label || ''))).map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Expected Delivery Date</label>
                <input
                  type="date"
                  value={editForm.expectedDeliveryDate}
                  onChange={(e) => setEditForm({ ...editForm, expectedDeliveryDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Tracking Number</label>
                <input
                  type="text"
                  value={editForm.trackingNumber}
                  onChange={(e) => setEditForm({ ...editForm, trackingNumber: e.target.value })}
                  placeholder="Enter tracking number"
                />
              </div>
              <div className="form-group">
                <label>Delivery Notes (visible to customer)</label>
                <textarea
                  value={editForm.deliveryNotes}
                  onChange={(e) => setEditForm({ ...editForm, deliveryNotes: e.target.value })}
                  placeholder="Notes about delivery (e.g., special instructions)"
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>Internal Notes (for history)</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Reason for update (internal record)"
                  rows={2}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" type="button" onClick={() => setShowEditModal(false)} disabled={saving}>
                Close
              </button>
              <button 
                className="save-btn" 
                onClick={handleUpdateDelivery}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="modal-overlay" onClick={() => !rejecting && setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Order</h3>
              <button className="close-btn" type="button" onClick={() => setShowRejectModal(false)} disabled={rejecting}>×</button>
            </div>
            <div className="modal-body">
              <p className="reject-help">Add a comment for the customer. Points used or earned on this order will be reversed.</p>
              <div className="form-group">
                <label>Rejection comment *</label>
                <textarea
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  placeholder="e.g. Item out of stock, unable to fulfill this order"
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" type="button" onClick={() => setShowRejectModal(false)} disabled={rejecting}>
                Close
              </button>
              <button
                type="button"
                className="reject-offline-btn"
                onClick={handleRejectOrder}
                disabled={rejecting}
              >
                {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvoice && (
        <div className="modal-overlay invoice-overlay no-print-overlay" onClick={() => setShowInvoice(false)}>
          <div className="modal-content invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header no-print">
              <h3>Order Invoice</h3>
              <div className="invoice-modal-actions">
                <button type="button" className="invoice-btn" onClick={printInvoice} disabled={!invoice}>Print</button>
                <button type="button" className="close-btn" onClick={() => setShowInvoice(false)}>×</button>
              </div>
            </div>
            <div className="invoice-body">
              {invoiceLoading && <p>Loading invoice...</p>}
              {invoice && (
                <div className="seller-invoice" id="seller-invoice-print">
                  <div className="invoice-top">
                    <div>
                      <h2>Yelooo</h2>
                      <p className="invoice-tag">Tax Invoice</p>
                    </div>
                    <div className="invoice-meta">
                      <strong>{invoice.invoiceNumber}</strong>
                      <span>{formatDate(invoice.invoiceDate)}</span>
                      <span>{invoice.orderType} · {invoice.status}</span>
                    </div>
                  </div>
                  <div className="invoice-parties">
                    <div>
                      <h4>Sold by</h4>
                      <p>{invoice.seller?.shopName || invoice.seller?.ownerName}</p>
                      {invoice.seller?.ownerName && invoice.seller?.shopName && <p>{invoice.seller.ownerName}</p>}
                      {invoice.seller?.sellerCode && <p>Seller code: {invoice.seller.sellerCode}</p>}
                      {invoice.seller?.gstNumber && <p>GSTIN: {invoice.seller.gstNumber}</p>}
                      {invoice.seller?.phone && <p>Phone: {invoice.seller.phone}</p>}
                      <p>
                        {[invoice.seller?.address, invoice.seller?.city, invoice.seller?.state, invoice.seller?.pinCode]
                          .filter(Boolean).join(', ')}
                      </p>
                    </div>
                    <div>
                      <h4>Bill to</h4>
                      <p>{invoice.customer?.name}</p>
                      {invoice.customer?.userCode && <p>Customer code: {invoice.customer.userCode}</p>}
                      {invoice.customer?.phone && <p>Phone: {invoice.customer.phone}</p>}
                      {invoice.customer?.email && <p>{invoice.customer.email}</p>}
                      <p>
                        {[invoice.customer?.address, invoice.customer?.addressLine2, invoice.customer?.city, invoice.customer?.state, invoice.customer?.pinCode]
                          .filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                  {invoice.transactionReference && (
                    <p className="invoice-ref">Reference: {invoice.transactionReference}</p>
                  )}
                  <table className="invoice-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Delivery</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(invoice.items) ? invoice.items : (invoice.items?.$values || [])).map((line, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            {line.productName}
                            {(line.sizeName || line.colorName) && (
                              <div className="invoice-variant">
                                {[line.sizeName && `Size: ${line.sizeName}`, line.colorName && `Color: ${line.colorName}`].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </td>
                          <td>{line.quantity}</td>
                          <td>₹{Number(line.unitPrice || 0).toFixed(2)}</td>
                          <td>₹{Number(line.deliveryCharge || 0).toFixed(2)}</td>
                          <td>₹{Number(line.lineTotal || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="invoice-totals">
                    <div><span>Subtotal</span><span>₹{Number(invoice.subtotal || 0).toFixed(2)}</span></div>
                    <div><span>Delivery</span><span>₹{Number(invoice.deliveryTotal || 0).toFixed(2)}</span></div>
                    <div className="grand"><span>Total</span><span>₹{Number(invoice.grandTotal || 0).toFixed(2)}</span></div>
                  </div>
                  <p className="invoice-foot">This is a computer-generated invoice for the seller portion of the order.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerOrdersPage;
